import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';

/**
 * A form draft in localStorage that survives a crash or a closed tab (#3404).
 *
 * The key carries the tenant and the user, because workshop terminals are
 * shared: a draft must never open in somebody else's session. A draft older
 * than `maxAgeMs` is dropped silently. Saving is debounced, so typing does not
 * write on every keystroke. Built for the create work-order dialog first and
 * meant for the other long dialogs later (quote, additional work, #557).
 */

const PREFIX = 'os-buddy:draft';
const DEFAULT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const SAVE_DELAY_MS = 400;

export interface StoredDraft<T> {
  version: number;
  savedAt: string;
  data: T;
}

export interface FormDraft<T> {
  /** The stored draft, or null when there is none, it is stale or unreadable. */
  load: () => StoredDraft<T> | null;
  /** Debounced write of the current form state. */
  save: (data: T) => void;
  /** Removes the draft, e.g. after a successful create or an explicit discard. */
  clear: () => void;
}

export function formDraftKey(form: string, tenantId: string, userId: string): string {
  return `${PREFIX}:${tenantId}:${userId}:${form}`;
}

export function useFormDraft<T>(
  form: string,
  options: { version?: number; maxAgeMs?: number } = {},
): FormDraft<T> {
  const version = options.version ?? 1;
  const maxAgeMs = options.maxAgeMs ?? DEFAULT_MAX_AGE_MS;
  const userId = useAuthStore((s) => s.user?.id);
  const tenantId = useAuthStore((s) => s.user?.tenantId);
  const key = userId && tenantId ? formDraftKey(form, tenantId, userId) : null;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPending = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => cancelPending, [cancelPending]);

  const load = useCallback((): StoredDraft<T> | null => {
    if (!key) return null;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as StoredDraft<T>;
      const age = Date.now() - new Date(parsed.savedAt).getTime();
      if (parsed.version !== version || !Number.isFinite(age) || age > maxAgeMs) {
        localStorage.removeItem(key);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }, [key, version, maxAgeMs]);

  const save = useCallback((data: T) => {
    if (!key) return;
    cancelPending();
    timer.current = setTimeout(() => {
      timer.current = null;
      try {
        const draft: StoredDraft<T> = { version, savedAt: new Date().toISOString(), data };
        localStorage.setItem(key, JSON.stringify(draft));
      } catch {
        // Storage full or blocked: the form still works, it just is not saved.
      }
    }, SAVE_DELAY_MS);
  }, [key, version, cancelPending]);

  const clear = useCallback(() => {
    cancelPending();
    if (!key) return;
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }, [key, cancelPending]);

  return useMemo(() => ({ load, save, clear }), [load, save, clear]);
}
